// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {defineStore} from 'pinia'
import {ProductInterface} from "@/core/types";

import {usePaginationStore} from "@/store/SerpPage/PaginationStore";
import {useSerpResultsStore} from "@/store/SerpPage/SerpResultsStore";
import {useFiltersStore} from "@/store/SerpPage/FiltersStore";
import {useSearchQueryStore} from "@/store/SearchQueryStore";
import {usePopupStore} from "@/store/AutocompletePopup/PopupStore";
import {useUserRecentQueriesStore} from "@/store/AutocompletePopup/UserRecentQueriesStore";
import {useSortingStore} from "@/store/SerpPage/SortingStore";

export const useUrlStore = defineStore('UrlStore', {
  state: () => {
    return {
      products: [] as ProductInterface[],
      filters: {},
      price: {},
      sort: '',
      query: '',
      total: null,
      page: 0,
      totalPage: null,
      loading: false,
      initialised: false,
      urlStore: window.location.href
    }
  },
  getters: {},
  actions: {
    
    composeUrlParams(url: URL) {
      const preparedUrl = new URL(url)
      const isHashBeforeSearchParams = useSearchQueryStore().getIsHashBeforeSearchParams;
      if (!isHashBeforeSearchParams) {
        return preparedUrl.toString()
      }
      const hashParams = preparedUrl.searchParams ? preparedUrl.searchParams.toString() : '';
      preparedUrl.hash = '';
      preparedUrl.search = '';
      if (!hashParams) {
        return preparedUrl.href
      }
      
      return `${preparedUrl.href}#?${hashParams}`
    },
    
    decomposeUrl() {
      const url = new URL(location.toString());
      const isHashBeforeSearchParams = useSearchQueryStore().getIsHashBeforeSearchParams;
      const rawHash = location.hash || ''
      const shouldParseHash = isHashBeforeSearchParams || rawHash.startsWith('#?')
      if (shouldParseHash) {
        const normalizedHash = rawHash.startsWith('#?')
          ? rawHash.substring(2)
          : rawHash.startsWith('#')
            ? rawHash.substring(1)
            : rawHash
        if (normalizedHash) {
          const params = new URLSearchParams(normalizedHash);
          for (const [key, value] of params) {
            url.searchParams.append(key, value)
          }
        }
      }
      return url;
    },
    
    updateUrl(url: URL, redirectUrl = null) {
      const needRedirect = redirectUrl && location.pathname !== redirectUrl
      const preparedUrl = this.composeUrlParams(url)
      if (needRedirect || !window.history) {
        window.location.href = preparedUrl
        return
      }
      window.history.pushState({path: preparedUrl}, '', preparedUrl)
    },
    
    async goToSerpPage(redirectionUrl: string, query: string | '' = '') {
      const targetPath = redirectionUrl || '/'
      const redirectPathname = targetPath.split('?')[0]
      const targetUrl = new URL(window.location.origin + targetPath)
      const userRecentQueriesStore = useUserRecentQueriesStore()
      const filtersStore = useFiltersStore()
      const serpStore = useSerpResultsStore()
      const paginationStore = usePaginationStore();
      const queryStore = useSearchQueryStore();
      const popupStore = usePopupStore();
      const sortingStore = useSortingStore();
      if (query) {
        queryStore.updateQuery(query)
        userRecentQueriesStore.addQuery(query, { source: 'manual' })
      }
      const normalizedQuery = queryStore.getSearchQuery
      if (normalizedQuery) {
        targetUrl.searchParams.set('q', normalizedQuery)
      } else {
        targetUrl.searchParams.delete('q')
      }
      this.updateUrl(targetUrl, redirectPathname)
      
      popupStore.hideAutocomplete()
      paginationStore.resetPageValue()
      sortingStore.syncSelectedSortFromUrl({skipWatcher: true})
      
      await filtersStore.fetchFiltersAnProducts()
      if (typeof window.afterGoToSerpPage !== 'undefined') {
        window.afterGoToSerpPage()
      }
      serpStore.scrollIntoView()
    },
    
    removePageParameter(url: URL): void {
      url.searchParams.delete('p');
    },
    getFilterName(filterKey: string): void{
      const regex = /f\[(.*?)]/;
      const  filterName = regex.exec(filterKey);
      if(filterName){
        filterStore.clearInputValues(filterName[1])
      }
    },
    removeFiltersFromUrl() {
      const url = this.decomposeUrl()
      const preservedEntries: Array<[string, string]> = []

      for (const key of ['q', 's']) {
        const value = url.searchParams.get(key)
        if (value !== null) {
          preservedEntries.push([key, value])
        }
      }

      url.search = ''

      preservedEntries.forEach(([key, value]) => {
        url.searchParams.set(key, value)
      })

      this.updateUrl(url)
    },
    addFilterToParameters(url: URL, field: string, filterValue: string): void {
      url.searchParams.set(`f[${field}]`, JSON.stringify(filterValue));
    },
    removeFilterFromParameters(url: URL, field: string): void {
      url.searchParams.delete(`f[${field}]`);
    },
    
    updateFiltersParameters(url: URL, field: string, filterValue: string): void {
      if (filterValue.length > 0) {
        this.addFilterToParameters(url, field, filterValue)
      } else {
        this.removeFilterFromParameters(url, field)
      }
    },
    
    removeRangeFilterFromParameters(url: URL, field: string): void {
      url.searchParams.delete(`f[${field}]`);
    },
    
    addRangeFiltersToParameters(url: URL, field: string, rangeData): void {
      url.searchParams.set(`f[${field}]`, JSON.stringify(rangeData));
    },
    
    updateSortParameter(paramValue: string) {
      const url = useUrlStore().decomposeUrl()
      url.searchParams.set('s', paramValue)
      this.updateUrl(url)
    },
    
    async clearQuery(redirectionUrl: string, autocomplete: boolean) {
      const url = useUrlStore().decomposeUrl()
      const queryStore = useSearchQueryStore()
      url.searchParams.delete('q')
      queryStore.clearQuery()
      if (autocomplete) {
        return
      }
      this.updateUrl(url)
      await this.goToSerpPage(redirectionUrl)
    },
    
    updatePageParameter(page: number): void {
      const url = useUrlStore().decomposeUrl()
      if (page === 0) {
        this.removePageParameter(url)
      } else {
        url.searchParams.set('p', page);
      }
      this.updateUrl(url);
    },
    
  },
})
